import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import moment from 'moment';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { getLogServicesApi } from '../../api/LogServiceApi';
import colorsDark from '../../assets/colors/dark';
import { NAMESPACE, DECODED_ACCESS_TOKEN } from '../../constants/system.keys';
import storageService from '../../services/storage.service';

const getListTimeInRange = (startTimeMs, endTimeMs, periodMs = moment.duration(1, 'hour').asMilliseconds()) => {
    const listTime = [];
    let curTime = startTimeMs;
    while (curTime < endTimeMs) {
        listTime.push(curTime);
        curTime += periodMs;
    }
    return listTime;
}

const LogEventLineChart = forwardRef(({ dataDevice, baseUrl, dataFilterType, dataRangerGateway, chartPeriodMs = moment.duration(1, 'hour').asMilliseconds() }, ref) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [dataSummaryLog, setDataSummaryLog] = useState({});
    const [listTimeStatistic, setListTimeStatistic] = useState([]);

    useEffect(() => {
        if (dataDevice) {
            setIsLoading(true);
            fetchDataSummaryLog(dataDevice?.serial, dataFilterType, dataRangerGateway[0], dataRangerGateway[1]);
        }
    }, [dataRangerGateway]);

    useEffect(() => {
        setListTimeStatistic(getListTimeInRange(dataRangerGateway[0], dataRangerGateway[1], chartPeriodMs));
    }, [chartPeriodMs, dataRangerGateway]);

    useEffect(() => {
        return () => {
            setDataSummaryLog({});
        }
    }, []);

    useImperativeHandle(ref, () => {
        return {
            refreshChartData() {
                setIsLoading(true);
                fetchDataSummaryLog(dataDevice?.serial, dataFilterType, dataRangerGateway[0], dataRangerGateway[1]);
            },
        };
    }, [dataRangerGateway]);

    const fetchDataSummaryLog = async (serial, type, fromTime, toTime) => {
        const token = storageService.getInternalStorageByKey(DECODED_ACCESS_TOKEN);
        const namespace = localStorage.getItem(NAMESPACE)
        const dataQuery = {
            serial,
            fromTime: fromTime ? Math.floor(fromTime / 1000) : Math.floor(moment().startOf('day').valueOf() / 1000),
            toTime: toTime ? Math.floor(toTime / 1000) : Math.floor(moment().endOf('day').valueOf() / 1000),
            limit: 300,
            offset: 0,
            isDownload: 0,
            customerNamespace: namespace
        }
        try {
            const dataRes = await getLogServicesApi(baseUrl, token, { namespace }, dataQuery);
            const dataRecordsTemp = _.uniqBy(dataRes?.data?.records ?? [], function (e) {
                return e.timestamp;
            });
            setDataSummaryLog(dataRes?.data ? {
                ...dataRes?.data,
                records: dataRecordsTemp
            } : {});
            setIsLoading(false);
        } catch (error) {
            setDataSummaryLog({});
            setIsLoading(false);
        }
    }

    return (
        <div className="log-event-line-chart-wrapper">
            {listTimeStatistic.length > 0 && <div className='statistic-chart-box'>
                <HighchartsReact
                    isLoading={isLoading}
                    highcharts={Highcharts}
                    options={{
                        chart: {
                            type: 'area',
                            zoomType: 'x',
                            backgroundColor: '#141414',
                        },
                        title: {
                            text: '',
                            style: {
                                color: colorsDark.$light
                            }
                        },
                        xAxis: {
                            categories: listTimeStatistic.map((timeMs, index) => {
                                return moment(timeMs).format(chartPeriodMs === moment.duration(1, 'day').asMilliseconds() ? "DD-MM-YYYY" : "HH:mm DD-MM-YYYY");
                            }),
                        },
                        yAxis: {
                            min: 0,
                            title: {
                                text: t('Average')
                            },
                            gridLineColor: 'transparent',
                        },
                        legend: {
                            enabled: false
                        },
                        plotOptions: {
                            area: {
                                fillColor: {
                                    linearGradient: {
                                        x1: 0,
                                        y1: 0,
                                        x2: 0,
                                        y2: 1
                                    },
                                    stops: [
                                        [0, Highcharts?.color(colorsDark.$online ?? '')?.setOpacity(0.3)?.get('rgba')],
                                        [1, Highcharts?.color(colorsDark.$online ?? '')?.setOpacity(0)?.get('rgba')]
                                    ]
                                },
                                marker: {
                                    radius: 2,
                                    fillColor: colorsDark.$online
                                },
                                lineWidth: 1,
                                lineColor: colorsDark.$online,
                                states: {
                                    hover: {
                                        lineWidth: 1
                                    }
                                },
                                threshold: null
                            }
                        },
                        tooltip: {
                            pointFormat: `<span>{series.name}</span>: <b>{point.y} ${t('customers')}</b><br/>`,
                            borderColor: colorsDark.$online,
                        },
                        series: [{
                            name: t('Average'),
                            data: listTimeStatistic.map((time, index) => {
                                const inPeriodItems = dataSummaryLog?.records?.filter(data => {
                                    const curTimestampMs = data.timestamp * 1000;
                                    return curTimestampMs >= time && (index === listTimeStatistic.length - 1 || curTimestampMs < listTimeStatistic[index + 1])
                                });
                                const dataTemp = inPeriodItems?.sort((a, b) => (Number(b.count) || 0) - Number(a.count))
                                return {
                                    name: `${t('From')}: ${moment(time).format("HH:mm DD-MM-YYYY")}</br>${t('To')}: ${moment(time + chartPeriodMs).format("HH:mm DD-MM-YYYY")}`,
                                    y: inPeriodItems?.length > 0 ? Number((dataTemp?.[0]?.count || 0)) : 0,
                                };
                            }),
                        }]
                    }}
                />
            </div>}
        </div>
    );
});

export default LogEventLineChart;
